import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Paper,
  Chip,
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterSectionTitleSx,
  masterPrimaryBtnSx,
  masterFieldSx,
} from '../../common/MasterPageShell';

const TicketSettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [formData, setFormData] = useState({ startTicketNo: '0001' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/ticket-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success' && response.data.data) {
        const data = response.data.data;
        setSettings(data);
        setFormData({ startTicketNo: data.startTicketNo || '0001' });
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch ticket settings');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.trim() }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.startTicketNo || formData.startTicketNo.trim() === '') {
      setSaveError('Start ticket number cannot be empty');
      return;
    }
    setSaveLoading(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${baseURL}/ticket-settings`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.data.status === 'success') {
        setSettings(response.data.data);
        setSaveSuccess(true);
      }
      setSaveLoading(false);
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save ticket settings');
      setSaveLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSaveSuccess(false);
    setSaveError(null);
  };

  return (
    <MasterPageShell title="Ticket Settings" subtitle="Master · Numbering" loading={loading}>
      {error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography sx={masterSectionTitleSx}>Start ticket number</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                New pledges continue from this number. Letters and digits are allowed.
              </Typography>
              <Box component="form" onSubmit={handleSave} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="startTicketNo"
                  label="Start ticket number"
                  name="startTicketNo"
                  value={formData.startTicketNo}
                  onChange={handleInputChange}
                  sx={masterFieldSx}
                />
                <Button type="submit" fullWidth variant="contained" sx={masterPrimaryBtnSx} disabled={saveLoading}>
                  {saveLoading ? 'Saving…' : 'Save settings'}
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography sx={masterSectionTitleSx}>Current</Typography>
              <Chip
                label={`Start: ${settings?.startTicketNo || formData.startTicketNo || '—'}`}
                sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}
              />
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar open={saveSuccess} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', borderRadius: 2 }}>
          Ticket settings saved
        </Alert>
      </Snackbar>
      <Snackbar open={!!saveError} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          {saveError}
        </Alert>
      </Snackbar>
    </MasterPageShell>
  );
};

export default TicketSettingsManagement;
